import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import {
  BatchLabelCatalogItem,
  CreateBatchLabelRequest,
  UpdateBatchCatalogLabelRequest,
  DeleteBatchCatalogLabelRequest,
} from "../../../../@types/API/IBatchLabel";
import { getLocalizedApiName } from "../../../../utils/i18n/getLocalizedApiName";
import { useAuth } from "../../../../hooks/useAuth";
import axios from "../../../../services/axios";
import { ButtonV2, InputV2, SearchInputV2 } from "../../../FormControlsV2";
import { TableFilterRow, TableViewport } from "../../TableLayout";
import { ReactComponent as EditIcon } from "../../../../assets/svg/icons/penEdit.svg";
import { ReactComponent as TrashIcon } from "../../../../assets/svg/icons/trash.svg";
import { ReactComponent as CloseIcon } from "../../../../assets/svg/icons/close.svg";
import "../../../Table/TaskEvaluation/styles.css";

type FieldErrors = { name?: string };

function normalizeSearch(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function validationDetails(data: any): string[] {
  const details = data?.errors;
  if (Array.isArray(details))
    return details.filter((item): item is string => typeof item === "string");
  if (details && typeof details === "object") {
    return Object.values(details).reduce<string[]>((all, value) => {
      if (typeof value === "string") all.push(value);
      if (Array.isArray(value))
        all.push(
          ...value.filter((item): item is string => typeof item === "string"),
        );
      return all;
    }, []);
  }
  return [];
}

export function BatchLabelsManagement({
  isCreating,
  onClose,
}: {
  isCreating: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canCreate = user?.role === "MASTER";
  const [labels, setLabels] = useState<BatchLabelCatalogItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [name, setName] = useState("");
  const [hasStamp, setHasStamp] = useState(false);
  const [editingLabel, setEditingLabel] =
    useState<BatchLabelCatalogItem | null>(null);
  const [editOriginalName, setEditOriginalName] = useState("");
  const [deletingLabel, setDeletingLabel] =
    useState<BatchLabelCatalogItem | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState("");
  const [serverDetails, setServerDetails] = useState<string[]>([]);
  const [successKey, setSuccessKey] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteErrorKey, setDeleteErrorKey] = useState("");
  const dialogRef = useRef<HTMLElement>(null);
  const deleteDialogRef = useRef<HTMLElement>(null);
  const formOpen = isCreating || editingLabel != null;
  const editChanged =
    editingLabel != null &&
    (name.trim() !== editOriginalName ||
      hasStamp !== editingLabel.stamp_options.length > 0);
  const filteredLabels = useMemo(() => {
    const query = normalizeSearch(searchQuery.trim());
    if (!query) return labels.map((label) => ({ label, batches: label.batches }));
    return labels.flatMap((label) => {
      const matchingBatches = label.batches.filter((batch) =>
        [
          batch.farm?.name,
          batch.farm?.initials,
          batch.area?.name,
          batch.area?.initials,
        ].some((value) => value && normalizeSearch(value).includes(query)),
      );
      const matchesName = Object.values(label.name).some((value) =>
        normalizeSearch(value).includes(query),
      );
      if (!matchesName && matchingBatches.length === 0) return [];
      return [{
        label,
        batches: matchingBatches.length ? matchingBatches : label.batches,
      }];
    });
  }, [labels, searchQuery]);

  const loadLabels = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { data } =
        await axios.get<BatchLabelCatalogItem[]>("/batches/labels");
      setLabels(data);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadLabels();
  }, [loadLabels]);

  const resetForm = useCallback(() => {
    setName("");
    setEditOriginalName("");
    setHasStamp(false);
    setErrors({});
    setSubmitError("");
    setServerDetails([]);
    if (isCreating) onClose();
    setEditingLabel(null);
  }, [isCreating, onClose]);

  function openEdit(label: BatchLabelCatalogItem) {
    const localizedName = getLocalizedApiName(label.name, i18n.language);
    setEditingLabel(label);
    setName(localizedName);
    setEditOriginalName(localizedName);
    setHasStamp(label.stamp_options.length > 0);
    setErrors({});
    setSubmitError("");
    setServerDetails([]);
    setSuccessKey("");
  }

  function openDelete(label: BatchLabelCatalogItem) {
    setDeletingLabel(label);
    setDeleteErrorKey("");
    setSuccessKey("");
  }

  useEffect(() => {
    if (!formOpen || !canCreate) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    dialogRef.current
      ?.querySelector<HTMLInputElement>('input[name="batch-label-name"]')
      ?.focus();
    return () => previousFocus?.focus();
  }, [formOpen, canCreate]);

  useEffect(() => {
    if (!formOpen || !canCreate) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) {
        event.preventDefault();
        resetForm();
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          "button:not([disabled]), input:not([disabled])",
        ) ?? [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [formOpen, canCreate, submitting, resetForm]);

  useEffect(() => {
    if (!deletingLabel) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    deleteDialogRef.current
      ?.querySelector<HTMLButtonElement>("button")
      ?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !deleting) setDeletingLabel(null);
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        deleteDialogRef.current?.querySelectorAll<HTMLButtonElement>(
          "button:not([disabled])",
        ) ?? [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus();
    };
  }, [deletingLabel, deleting]);

  function validate(): boolean {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = "batchLabelsManagement.requiredName";
    else if (name.trim().length > 255)
      next.name = "batchLabelsManagement.nameTooLong";
    setErrors(next);
    return !next.name;
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canCreate || submitting || !validate()) return;
    const trimmedName = name.trim();
    const editPayload: UpdateBatchCatalogLabelRequest | null = editingLabel
      ? { id: editingLabel.id }
      : null;
    if (editPayload && editingLabel) {
      if (trimmedName !== editOriginalName) editPayload.name = trimmedName;
      if (hasStamp !== editingLabel.stamp_options.length > 0)
        editPayload.has_stamp = hasStamp;
      if (Object.keys(editPayload).length === 1) return;
    }
    const createPayload: CreateBatchLabelRequest = {
      name: trimmedName,
      has_stamp: hasStamp,
    };
    setSubmitting(true);
    setSubmitError("");
    setServerDetails([]);
    setSuccessKey("");
    try {
      if (editPayload) await axios.patch("/batches/labels", editPayload);
      else await axios.post("/batches/labels", createPayload);
      resetForm();
      setSuccessKey(
        editPayload
          ? "batchLabelsManagement.updated"
          : "batchLabelsManagement.created",
      );
      await loadLabels();
    } catch (error: any) {
      const status = error?.response?.status;
      setSubmitError(
        status === 409 && editPayload
          ? "batchLabelsManagement.editConflict"
          : status === 400
            ? editPayload
              ? "batchLabelsManagement.editInvalid"
              : "batchLabelsManagement.invalid"
            : status === 403
              ? editPayload
                ? "batchLabelsManagement.manageForbidden"
                : "batchLabelsManagement.forbidden"
              : status === 404 && editPayload
                ? "batchLabelsManagement.notFound"
                : editPayload
                  ? "batchLabelsManagement.editError"
                  : "batchLabelsManagement.createError",
      );
      if (status === 400) {
        const data = error?.response?.data;
        const details = validationDetails(data);
        if (!details.length && typeof data?.message === "string")
          details.push(data.message);
        setServerDetails(details);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteLabel() {
    if (!canCreate || !deletingLabel || deleting) return;
    const payload: DeleteBatchCatalogLabelRequest = { id: deletingLabel.id };
    setDeleting(true);
    setDeleteErrorKey("");
    setSuccessKey("");
    try {
      await axios.delete("/batches/labels", { data: payload });
      setDeletingLabel(null);
      setSuccessKey("batchLabelsManagement.deleted");
      await loadLabels();
    } catch (error: any) {
      const status = error?.response?.status;
      setDeleteErrorKey(
        status === 403
          ? "batchLabelsManagement.manageForbidden"
          : status === 404
            ? "batchLabelsManagement.notFound"
            : "batchLabelsManagement.deleteError",
      );
    } finally {
      setDeleting(false);
    }
  }

  return (
    <main className="batch-labels-management">
      {canCreate &&
        formOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="batch-labels-management__overlay"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !submitting)
                resetForm();
            }}
          >
            <section
              className="batch-labels-management__dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="batch-labels-dialog-title"
              aria-describedby="batch-labels-dialog-help"
              ref={dialogRef}
            >
              <form
                className="batch-labels-management__form"
                onSubmit={submit}
                noValidate
              >
                <div className="batch-labels-management__dialog-heading">
                  <h3 id="batch-labels-dialog-title">
                    {t(
                      editingLabel
                        ? "batchLabelsManagement.edit"
                        : "batchLabelsManagement.create",
                    )}
                  </h3>
                  <button
                    type="button"
                    className="batch-labels-management__close"
                    onClick={resetForm}
                    disabled={submitting}
                    aria-label={t(
                      editingLabel
                        ? "batchLabelsManagement.closeEdit"
                        : "batchLabelsManagement.close",
                    )}
                  >
                    <CloseIcon aria-hidden="true" />
                  </button>
                </div>

                <InputV2
                  name="batch-label-name"
                  label={t("batchLabelsManagement.labelName")}
                  value={name}
                  maxLength={255}
                  error={errors.name ? t(errors.name) : undefined}
                  onChange={(value) => {
                    setName(value);
                    setErrors((current) => ({ ...current, name: "" }));
                  }}
                />
                <label
                  className={[
                    "ov2-product-creep-feeding",
                    "batch-labels-management__stamp",
                    hasStamp && "ov2-product-creep-feeding--checked",
                    submitting && "ov2-product-creep-feeding--disabled",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <input
                    type="checkbox"
                    name="batch-label-has-stamp"
                    checked={hasStamp}
                    disabled={submitting}
                    onChange={(event) => setHasStamp(event.target.checked)}
                  />
                  <span>{t("batchLabelsManagement.hasStamp")}</span>
                </label>
                {submitError && (
                  <div role="alert" className="batch-labels-management__error">
                    <p>{t(submitError)}</p>
                    {serverDetails.length > 0 && (
                      <ul>
                        {serverDetails.map((detail, index) => (
                          <li key={index}>{detail}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <div className="batch-labels-management__actions">
                  <ButtonV2
                    type="submit"
                    disabled={
                      submitting || (editingLabel != null && !editChanged)
                    }
                  >
                    {submitting
                      ? t(
                          editingLabel
                            ? "batchLabelsManagement.updating"
                            : "batchLabelsManagement.saving",
                        )
                      : t(
                          editingLabel
                            ? "batchLabelsManagement.saveChanges"
                            : "batchLabelsManagement.save",
                        )}
                  </ButtonV2>
                  <ButtonV2
                    variant="secondary"
                    disabled={submitting}
                    onClick={resetForm}
                  >
                    {t("batchLabelsManagement.cancel")}
                  </ButtonV2>
                </div>
              </form>
            </section>
          </div>,
          document.body,
        )}
      {canCreate &&
        deletingLabel &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="batch-labels-management__overlay"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !deleting)
                setDeletingLabel(null);
            }}
          >
            <section
              className="batch-labels-management__dialog batch-labels-management__delete-dialog"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="batch-labels-delete-title"
              aria-describedby="batch-labels-delete-help"
              ref={deleteDialogRef}
            >
              <div className="batch-labels-management__form">
                <h3 id="batch-labels-delete-title">
                  {t("batchLabelsManagement.deleteTitle")}
                </h3>
                <p id="batch-labels-delete-help">
                  {t("batchLabelsManagement.deleteHelp", {
                    name: getLocalizedApiName(
                      deletingLabel.name,
                      i18n.language,
                    ),
                  })}
                </p>
                {deleteErrorKey && (
                  <p role="alert" className="batch-labels-management__error">
                    {t(deleteErrorKey)}
                  </p>
                )}
                <div className="batch-labels-management__actions">
                  <ButtonV2
                    onClick={() => {
                      void deleteLabel();
                    }}
                    disabled={deleting}
                  >
                    {t(
                      deleting
                        ? "batchLabelsManagement.deleting"
                        : "batchLabelsManagement.confirmDelete",
                    )}
                  </ButtonV2>
                  <ButtonV2
                    variant="secondary"
                    onClick={() => setDeletingLabel(null)}
                    disabled={deleting}
                  >
                    {t("batchLabelsManagement.cancel")}
                  </ButtonV2>
                </div>
              </div>
            </section>
          </div>,
          document.body,
        )}
      <TableFilterRow>
        <SearchInputV2
          className="batch-labels-management__search ov2-search-field--wide"
          name="batch-labels-search"
          label={t("batchLabelsManagement.searchLabel")}
          placeholder={t("batchLabelsManagement.searchPlaceholder")}
          disabled={loading || loadError || labels.length === 0}
          onSearch={(value) => setSearchQuery(value?.trim() ?? "")}
          onChange={(value) => {
            if (!value.trim()) setSearchQuery("");
          }}
        />
      </TableFilterRow>
      <TableViewport ariaLabel={t("batchLabelsManagement.catalog")}>
      {loading ? (
        <p role="status">{t("batchLabelsManagement.loading")}</p>
      ) : loadError ? (
        <div role="alert" className="batch-labels-management__error">
          <p>{t("batchLabelsManagement.loadError")}</p>
          <ButtonV2
            variant="secondary"
            onClick={() => {
              void loadLabels();
            }}
          >
            {t("batchLabelsManagement.retry")}
          </ButtonV2>
        </div>
      ) : labels.length === 0 ? (
        <p>{t("batchLabelsManagement.empty")}</p>
      ) : filteredLabels.length === 0 ? (
        <p>{t("batchLabelsManagement.noSearchResults")}</p>
      ) : (
          <table
            className={`tableTaskEvaluation batch-labels-management__table${canCreate ? "" : " batch-labels-management__table--readonly"}`}
          >
            <colgroup>
              <col className="batch-labels-management__name-column" />
              <col className="batch-labels-management__stamp-column" />
              <col className="batch-labels-management__batch-count-column" />
              {canCreate && (
                <col className="batch-labels-management__action-column" />
              )}
            </colgroup>
            <thead>
              <tr>
                <th scope="col">{t("batchLabelsManagement.labelColumn")}</th>
                <th scope="col">{t("batchLabelsManagement.stampColumn")}</th>
                <th scope="col">
                  {t("batchLabelsManagement.batchCountColumn")}
                </th>
                {canCreate && (
                  <th
                    scope="col"
                    aria-label={t("batchLabelsManagement.actions")}
                  />
                )}
              </tr>
            </thead>
            <tbody>
              {filteredLabels.map(({ label, batches }) => (
                <tr className="batch-labels-management__row" key={label.id}>
                  <td className="batch-labels-management__name">
                    {getLocalizedApiName(label.name, i18n.language)}
                  </td>
                  <td>
                    {t(
                      label.stamp_options.length > 0
                        ? "batchLabelsManagement.stampYes"
                        : "batchLabelsManagement.stampNo",
                    )}
                  </td>
                  <td>
                    {batches.length === 0 ? (
                      <span>{t("batchLabelsManagement.unused")}</span>
                    ) : batches.length === 1 ? (
                      <span>{batches[0].initials}</span>
                    ) : (
                      <span
                        className="batch-labels-management__batch-count"
                        title={batches
                          .map((batch) => batch.initials)
                          .join(", ")}
                        aria-label={t("batchLabelsManagement.batchesTooltip", {
                          count: batches.length,
                          initials: batches
                            .map((batch) => batch.initials)
                            .join(", "),
                        })}
                        tabIndex={0}
                      >
                        {t("batchLabelsManagement.batchCount", {
                          count: batches.length,
                          formattedCount: new Intl.NumberFormat(i18n.language).format(
                            batches.length,
                          ),
                        })}
                      </span>
                    )}
                  </td>
                  {canCreate && (
                    <td>
                      <span className="batch-labels-management__row-icons">
                        <button
                          type="button"
                          className="batch-labels-management__icon-button"
                          aria-label={t("batchLabelsManagement.editLabel", {
                            name: getLocalizedApiName(
                              label.name,
                              i18n.language,
                            ),
                          })}
                          onClick={() => openEdit(label)}
                        >
                          <EditIcon
                            className="batch-labels-management__edit-icon"
                            aria-hidden="true"
                          />
                        </button>
                        <button
                          type="button"
                          className="batch-labels-management__icon-button"
                          aria-label={t("batchLabelsManagement.deleteLabel", {
                            name: getLocalizedApiName(
                              label.name,
                              i18n.language,
                            ),
                          })}
                          onClick={() => openDelete(label)}
                        >
                          <TrashIcon
                            className="batch-labels-management__delete-icon"
                            aria-hidden="true"
                          />
                        </button>
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
      )}
      </TableViewport>
    </main>
  );
}
